import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Store } from '@ngrx/store';
import { take, filter } from 'rxjs/operators';
import { fsLoginInitiated } from 'src/app/STORE/selectors/applifecycle.selector';

@Injectable({
  providedIn: 'root'
})
export class FsBaseService {
  private injector = inject(Injector);
  private fireStore = inject(AngularFirestore);
  private store = inject(Store);

  /**
   * Helper to execute a callback only after Firestore login is confirmed.
   * Centralizing this check prevents listeners from failing unauthenticated.
   */
  public runAfterFsLogin(callback: () => void): void {
    this.store
      .select(fsLoginInitiated)
      .pipe(
        filter((fsLoggedIn) => fsLoggedIn === true),
        take(1)
      )
      .subscribe({
        next: () => {
          callback();
        },
        error: (err) => {
          console.error('FsBaseService: runAfterFsLogin error', err);
        }
      });
  }

  /**
   * Construct the query object for firestore listner
   * @param service - Object contains records limit
   * @param doc - firestore docid for single document search
   * @param queries - fileter query object
   * @param orderby - orderby object
   */
  public buildQueries(doc: any, queries: any, orderby: any, startafter: any, limit: any) {
    if (queries) {
      for (let i = 0; i < queries.length; i++) {
        doc.query = doc.query.where(queries[i].field, queries[i].operator || '==', queries[i].value);
      }
    }
    if (orderby) {
      if (Array.isArray(orderby)) {
        for (let i = 0; i < orderby.length; i++) {
          doc.query = doc.query.orderBy(orderby[i].field, orderby[i].dir);
        }
      } else {
        doc.query = doc.query.orderBy(orderby.field, orderby.dir);
      }
    }
    if (limit) {
      doc.query = doc.query.limit(Number(limit));
    }
    if (startafter) {
      doc.query = doc.query.startAfter(startafter);
    }
    return doc;
  }

  /**
   * Create the service and return the observable.
   * @param queryObj - Contains servvice object, docid, filer Array objects, orderby field objects
   */
  public fireService(queryObj: any) {
    return runInInjectionContext(this.injector, () => {
      let doc: any;
      doc = this.fireStore.collection(queryObj.collectionName);
      if (queryObj.docId) {
        doc = doc.doc(queryObj.docId);
      } else {
        doc = this.buildQueries(doc, queryObj.filter, queryObj.orderby, queryObj.startafter, queryObj.limit);
      }
      return doc.stateChanges();
    });
  }

  public fsValueChanges(queryObj: any) {
    return runInInjectionContext(this.injector, () => {
      let doc: any;
      doc = this.fireStore.collection(queryObj.collectionName);
      if (queryObj.docId) {
        doc = doc.doc(queryObj.docId);
      } else {
        doc = this.buildQueries(doc, queryObj.filter, queryObj.orderby, queryObj.startafter, queryObj.limit);
      }
      return doc.valueChanges();
    });
  }

  public updateDocument(queryObj: any) {
    return runInInjectionContext(this.injector, () =>
      this.fireStore.collection(queryObj.collectionName).doc(`/${queryObj.sid}`).update(queryObj.data)
    );
  }
}
